import React from "react";
import {
  format,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  parseISO,
  addDays,
  startOfDay,
  subDays,
  startOfWeek,
  startOfMonth,
  subMonths,
  formatISO,
} from "date-fns";
import { zeroDate } from "src/constants";

export const validEmail = (val: string): boolean =>
  Boolean(
    val &&
      /^[a-zA-Z0-9._%+-]+@(?!(?:[-.])|.*\.\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(
        val,
      ),
  );

export const validName = (val: string): boolean =>
  Boolean(val && /^[A-Za-zÀ-ÖØ-öø-ÿ '-]+$/.test(val));

export const validPhoneNumber = (value: string): boolean =>
  /^\d{10}$/.test(value);

export const isValidPhoneNumberWithCountryCode = (value: string): boolean =>
  /^\+1\d{10}$/.test(value);

export const shortMonthFromDate = (dateString: string): string => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "MMM");
};

export const dayOfMonthFromDate = (dateString: string): string => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "d");
};

export const dayAndDateFromDate = (dateString: string): string => {
  if (!dateString || dateString === zeroDate) return "";
  return format(addDays(parseISO(dateString), 1), "EEEE, MMMM dd");
};

export const timeAgo = (dateString: string): string => {
  if (dateString === zeroDate) return "----";
  const date = new Date(dateString);
  const now = new Date();

  const diffInYearsVal = differenceInYears(now, date);
  if (diffInYearsVal >= 1) {
    return format(date, "MM-dd-yyyy");
  }

  const diffInMonthsVal = differenceInMonths(now, date);
  if (diffInMonthsVal >= 1) {
    return `${diffInMonthsVal} month${diffInMonthsVal > 1 ? "s" : ""} ago`;
  }

  const diffInDaysVal = differenceInDays(now, date);
  if (diffInDaysVal >= 1) {
    return `${diffInDaysVal} day${diffInDaysVal > 1 ? "s" : ""} ago`;
  }

  const diffInHoursVal = differenceInHours(now, date);
  if (diffInHoursVal >= 1) {
    return `${diffInHoursVal} hour${diffInHoursVal > 1 ? "s" : ""} ago`;
  }

  const diffInMinutesVal = differenceInMinutes(now, date);
  if (diffInMinutesVal >= 1) {
    return `${diffInMinutesVal} minute${diffInMinutesVal > 1 ? "s" : ""} ago`;
  }

  return "Just now";
};

type DatePeriod =
  | "Today"
  | "Yesterday"
  | "This week"
  | "This month"
  | "Last month";

type DateRangeResult = {
  end: string | null;
  start: string | null;
  period?: string;
  error?: string;
};

export const dateRangefromDatePeriod = (
  period: DatePeriod,
): DateRangeResult => {
  const now = new Date();
  const end = formatISO(now, { representation: "date" }) + "T23:59:59.000Z";
  let start;

  switch (period) {
    case "Today":
      start =
        formatISO(startOfDay(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Yesterday":
      start =
        formatISO(startOfDay(subDays(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "This week":
      start =
        formatISO(startOfWeek(now, { weekStartsOn: 0 }), {
          representation: "date",
        }) + "T00:00:00.000Z"; // Assuming week starts on Sunday
      break;
    case "This month":
      start =
        formatISO(startOfMonth(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Last month":
      start =
        formatISO(startOfMonth(subMonths(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    default:
      return {
        end: null,
        start: null,
        error: `Invalid period: "${period}". Please use "Today", "Yesterday", "This week", "This month", or "Last month".`,
      };
  }

  return {
    end,
    start,
    period,
  };
};

export const formatDateRangeShort = (
  startDate: string,
  endDate: string,
): string => {
  if (!startDate || !endDate) {
    return "";
  }
  const parsedStartDate = addDays(parseISO(startDate), 1); // addDays is a dumb hack for UTC conversion
  const parsedEndDate = addDays(parseISO(endDate), 1); // addDays is a dumb hack for UTC conversion

  if (isNaN(parsedEndDate.getTime()) || isNaN(parsedEndDate.getTime())) {
    return ""; // Handle invalid date strings
  }

  const startMonth = format(parsedStartDate, "MMM");
  const startDay = format(parsedStartDate, "d");
  const endDay = format(parsedEndDate, "d");

  return `${startMonth} ${startDay}-${endDay}`;
};

// Can be used to create a date string in the format 'YYYY-MM-DD'
// Can also be used for the above function issues with UTC conversion.
export const createIsoDate = (
  year: number,
  month: number,
  day: number = 1,
): string => {
  const date = new Date(Date.UTC(year, month - 1, day)); // Create UTC date
  return date.toISOString(); // Stores as 'YYYY-MM-DDT00:00:00.000Z'
};

export const parseIsoDate = (isoDate: string) => {
  const date = new Date(isoDate);

  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // getUTCMonth() is 0-indexed (0 = Jan), so we add 1.
  const day = date.getUTCDate();

  return { year, month, day };
};
export const formatNumberForCurrency = (num: number): string => {
  return Number(num).toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatAddressTwoLines = (address: string): React.ReactElement => {
  const parts = address.split(", ");
  if (parts.length < 2)
    return React.createElement(React.Fragment, null, address);

  const streetAddress = parts[0];
  const cityStateZip = parts.slice(1).join(", ");

  return React.createElement(
    React.Fragment,
    null,
    streetAddress,
    React.createElement("br"),
    cityStateZip,
  );
};

export const snakeToCamel = (str: string): string =>
  str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace("-", "").replace("_", ""),
    );

export function objectSnakeToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => objectSnakeToCamel(item));
  }

  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  return Object.keys(obj).reduce(
    (acc, key) => {
      const convertedKey = snakeToCamel(key);
      acc[convertedKey] = objectSnakeToCamel(obj[key]);
      return acc;
    },
    {} as Record<string, any>,
  );
}

export const getSkipLosValue = (): boolean => {
  const environment = __ENV__;

  // Only skip LOS when explicitly in local environment
  // Default to false (enable LOS) for safety
  return environment === "local";
};

export const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;

  // Handle backend's null date representation
  if (dateString === "0001-01-01T00:00:00Z" || dateString === zeroDate)
    return false;

  const date = new Date(dateString);
  return !isNaN(date.getTime()) && date.getFullYear() > 1900;
};

export function normalizeUrl(url: string): string {
  // Return empty string for empty or whitespace-only input
  if (!url || url.trim() === "") {
    return "";
  }

  const trimmedUrl = url.trim();

  // Check if URL already has a scheme (protocol)
  // Scheme format: scheme://
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmedUrl);

  if (hasScheme) {
    // URL already has a scheme, return as-is
    return trimmedUrl;
  }

  // Auto-prepend https:// for URLs without scheme
  return `https://${trimmedUrl}`;
}

export function isValidUrlFormat(url: string): boolean {
  if (!url || url.trim() === "") {
    return true; // Empty URL is considered valid (optional field)
  }

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
